import React, { forwardRef, useLayoutEffect, useRef } from 'react';
import { IDatePicker } from './HBDatepicker.types';
import DatePicker from 'react-datepicker';
type ReactDatePickerProps = React.ComponentProps<typeof DatePicker>;
import { useLocale, useGetDateFormat } from '@honeybook/i18n-react';
import { getFirstDayOfWeek } from '@honeybook/i18n';
import { isAriaInvalid } from '../common/internal/isAriaInvalid';

const MAX_DATE_PICKER_DATE = new Date('Dec 31 2999');

const HBDatepickerBase = forwardRef<any, IDatePicker>((props, ref) => {
    const { locale } = useLocale();
    const { dateFormatUnicode, dateFormat: localeDateFormat } =
        useGetDateFormat();
    const {
        id,
        icon,
        value,
        selected,
        showYearPicker,
        onChange,
        dateFormat: dateFormatProp = dateFormatUnicode,
        placeholder = localeDateFormat,
        calendarStartDay,
        popperPlacement = 'bottom-start',
        className,
        maxDate = MAX_DATE_PICKER_DATE,
        decreaseMonthOnClick,
        increaseMonthOnClick,
        hasError,
        renderCustomHeader,
        onClickOutside,
        'aria-invalid': ariaInvalidProp,
        ...baseProps
    } = props;

    // react-datepicker types aria-invalid as a string, so the boolean form is
    // normalised here.
    const ariaInvalid = isAriaInvalid(hasError, ariaInvalidProp)
        ? 'true'
        : undefined;

    // When the display format is non-numeric (e.g. "d MMM yyyy"), typed numeric input
    // like "7/8/2026" never matches it and falls through to react-datepicker's US
    // new Date() constructor fallback. Passing an array lets react-datepicker try the
    // locale-numeric format first, so "7/8/2026" parses in locale order (DD/MM for
    // en-AU) instead of always resolving to US MM/DD order. The display uses [0].
    const dateFormat: string | string[] =
        dateFormatProp === dateFormatUnicode || Array.isArray(dateFormatProp)
            ? dateFormatProp
            : [dateFormatProp, dateFormatUnicode];

    const containerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!showYearPicker || !containerRef.current) {
            return;
        }

        const container = containerRef.current;

        const patch = () => {
            const yearWrapper = container.querySelector(
                '.react-datepicker__year-wrapper'
            );
            if (yearWrapper) {
                yearWrapper.setAttribute('role', 'listbox');
            }

            const selectedYear =
                selected instanceof Date
                    ? selected.getFullYear()
                    : value instanceof Date
                    ? value.getFullYear()
                    : null;

            container
                .querySelectorAll<HTMLElement>('.react-datepicker__year-text')
                .forEach(cell => {
                    const year = parseInt(cell.textContent?.trim() ?? '', 10);
                    cell.setAttribute('role', 'option');
                    cell.setAttribute('aria-label', `Choose ${year}`);
                    cell.setAttribute(
                        'aria-selected',
                        String(!isNaN(year) && year === selectedYear)
                    );
                });
        };

        patch();

        const observer = new MutationObserver(patch);
        observer.observe(container, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
        };
    }, [showYearPicker, selected, value]);

    // v9's ReactDatePickerProps is a nested discriminated union (selectsRange ×
    // selectsMultiple × showMonthYearDropdown) that TypeScript cannot narrow from a
    // spread of IDatePicker's flattened Omit type. The cast is safe: HBDatepicker
    // always uses single-date, non-range, non-multiple mode.
    const datepickerProps = {
        ...baseProps,
        calendarStartDay: calendarStartDay ?? getFirstDayOfWeek(locale),
        dateFormat,
        id,
        maxDate,
        onChange,
        placeholderText: placeholder,
        popperPlacement,
        renderCustomHeader,
        selected,
        showYearPicker,
        value: value as unknown as string,
        onClickOutside,
        autoComplete: 'off',
        'aria-invalid': ariaInvalid
    } as unknown as ReactDatePickerProps;

    return (
        <div ref={containerRef} style={{ display: 'contents' }}>
            <DatePicker {...datepickerProps} ref={ref} />
        </div>
    );
});

export default HBDatepickerBase;
